import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useUserRoles } from '@anytag/auth';
import { SelectOption } from '@anytag/shared-types';
import { SelectorField } from '@anytag/shared-molecules';
import { getSocialMediaText } from '@anytag/shared-utils';
import { Form, SocialIcon } from '@anytag/shared-atoms';
import { EngagementDetail, EngagementPostSocialAccount, SocialAccountType } from '@anytag/graphql';
import { useEngagementPostFormState } from '../hooks';
import { EngagementPostFormKeys, EngagementPostFormValues } from '../../types';

interface InfluencerAccountFieldsProps {
  engagementId: EngagementDetail['id'];
  isTtcmCampaign: EngagementDetail['isTtcmCampaign'];
}

export const InfluencerAccountFields = ({ engagementId, isTtcmCampaign }: InfluencerAccountFieldsProps) => {
  const { t } = useTranslation();
  const { isAdvertiser } = useUserRoles();
  const { setValue } = useFormContext<EngagementPostFormValues>();
  const { influencerOptions, availableSocialAccounts } = useEngagementPostFormState({ engagementId });

  const socialAccountOptions =
    availableSocialAccounts?.map<SelectOption<string> & Pick<EngagementPostSocialAccount, 'id' | 'socialMedia'>>(
      ({ name, socialMedia, id }) => ({
        id,
        socialMedia,
        value: `${id}-${socialMedia}`,
        label: name || getSocialMediaText(socialMedia),
        prefixIcon: <SocialIcon size={16} type={socialMedia} css={{ marginRight: '8px', display: 'inline-block' }} />
      })
    ) || [];

  return (
    <Form.Fields itemsPerRow={2}>
      <SelectorField<EngagementPostFormKeys>
        title={t('Selector.Influencer')}
        name="influencerId"
        disabled={isAdvertiser}
        options={influencerOptions}
        onChange={(field, value) => {
          setValue(field, value, { shouldValidate: true });
          setValue('selectedAccount', '');
          setValue('socialAccountId', '');
          setValue('socialAccountMedia', '');
          setValue('isTtcmForm', false);
        }}
        required
      />

      <SelectorField<EngagementPostFormKeys>
        name="selectedAccount"
        disabled={isAdvertiser}
        title={t('Selector.Select Social Account')}
        options={socialAccountOptions}
        onChange={(field, value) => {
          const [id, socialMedia] = value.split('-');
          setValue(field, value, { shouldValidate: true });
          setValue('socialAccountId', id || '');
          setValue('socialAccountMedia', (socialMedia as SocialAccountType) || '');
          setValue('isTtcmForm', isTtcmCampaign && socialMedia === SocialAccountType.TIKTOK);
        }}
        required
      />
    </Form.Fields>
  );
};
